/**
 * What this product calls each of the backend's value vocabularies.
 *
 * ## Why this file exists
 *
 * `app/models/uic.py` deliberately stores enum-ish values as plain strings, so
 * adding one is never a migration. The cost of that choice is that nothing
 * stands between a column value and the screen — and for a while nothing did.
 * The citation panel printed `element_severity_stands` under POLICIES
 * EVALUATED; `SeverityBadge` printed `threshold_breach` as a finding kind on
 * five different screens; the gate panel printed `request_revision` as a
 * decision.
 *
 * The workaround that grew instead was `.replace(/_/g, ' ')`, reinvented
 * independently in twelve files, three of which also reinvented the same
 * title-case pair beside it. That turns `element_severity_stands` into
 * "element severity stands", which is not jargon any more but is still not a
 * sentence, and it produced the specific absurdity of one vocabulary rendering
 * two ways eighty lines apart — the gate panel de-snaked these exact strings while
 * `CitationPanel`, importing from the same directory, did not.
 *
 * ## Why the copy lives here and not on the wire
 *
 * `ConfidenceBasis` already settled this question. It carries a `LABELS` dict
 * in `app/models/uic.py:175` *and* a mirror in `components/ui/progress.tsx`,
 * and the comment there gives the reason: a display label is copy, and copy
 * never varies per record, so sending it with every row buys nothing and costs
 * a field on every payload. This file is that decision applied to the rest of
 * the vocabularies.
 *
 * The one exception is `POLICY_OUTCOME_LABEL`, which mirrors
 * `PolicyOutcome.LABELS` in `app/models/uic.py`. Keep the two in step.
 *
 * ## How to use it
 *
 * Always go through `label()`. It falls back to `humanise()` rather than to the
 * raw string, so the day somebody adds a seventh `ArtifactKind` and forgets
 * this file, the screen reads "Fact sheet" rather than `fact_sheet`. A missing
 * entry should degrade to readable, not to a variable name.
 */

/** Sentence case from a snake_case or kebab-case identifier. */
export function humanise(value: string): string {
  const words = value.replace(/[_-]+/g, ' ').trim()
  if (!words) return ''
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * "a" or "an", for the word that follows it.
 *
 * The rail's status line read **"Gate 6 is waiting on a Administrator"** on
 * every case sitting at the last gate, because the article was a literal in the
 * template. `Administrator` is the one vowel-initial entry in
 * `gate_roles.ROLE_LABELS`, and it is Gate 6's role — so the one gate a
 * committee member is most likely to be looking at was the one that read wrong.
 *
 * Deliberately naive: five vowels, first letter, nothing else. English has
 * exceptions in both directions ("an hour", "a university") and a table of them
 * would be a second thing to maintain for words this codebase does not use. The
 * inputs are role labels from a closed set of four, and every one of them is
 * spelled the way the rule expects.
 */
export function article(word: string): string {
  return /^[aeiou]/i.test(word.trim()) ? 'an' : 'a'
}

/**
 * Title Case from a snake_case identifier.
 *
 * Replaces the `.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())` pair
 * that was written out three separate times across the policy components.
 */
export function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

/**
 * The label for a value, or a readable fallback.
 *
 * Empty string for null/undefined rather than "Unknown": a field the API did
 * not send is not a state the record is in, and inventing a word for it puts a
 * claim on screen that nothing supports.
 */
export function label(
  map: Record<string, string>,
  value?: string | null
): string {
  if (value === null || value === undefined || value === '') return ''
  return map[value] ?? humanise(value)
}

// ---------------------------------------------------------------------------
// Findings
// ---------------------------------------------------------------------------

/**
 * `FindingKind` — what sort of problem this is.
 *
 * `insufficient` becomes "Inadequate" because that is the word the rule rows
 * and the notice letters use for a document that was sent but does not meet the
 * standard. `threshold_breach` is the one that most needed a label: it means the
 * AI's own confidence fell below the floor a policy set, which is a statement
 * about the system rather than about the applicant, and no reader was going to
 * get that from the identifier.
 */
export const FINDING_KIND_LABEL: Record<string, string> = {
  missing: 'Missing',
  insufficient: 'Inadequate',
  inconsistent: 'Inconsistent',
  threshold_breach: 'Below the confidence floor',
  unverifiable: 'Could not be verified',
}

/**
 * `FindingStatus` — where a finding stands.
 *
 * These are deliberately **not** the words on the disposition control. That
 * control offers `Accept / Reject`, which are the *actions* a reviewer takes;
 * these are the *states* a finding is in afterwards. The citation panel used to
 * print the raw value in a badge two blocks above a control that mapped the same
 * values to verbs, which is why the panel read as though it were describing two
 * different things.
 *
 * `resolved` has no verb because no reviewer types it directly: it is written by
 * the notice layer when an applicant's response closes a deficiency item.
 */
export const FINDING_STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  rejected: 'Rejected',
  resolved: 'Resolved',
}

/**
 * The verbs on the disposition control, kept beside the states they produce.
 *
 * Two, not three. A "Needs discussion" pill wrote `escalated`, which the gate's
 * blocking count did not recognise — so flagging a critical finding for
 * discussion quietly stopped it holding the case, exactly as rejecting it would.
 * See `FindingStatus` in `app/models/uic.py` for why it was removed rather than
 * wired up.
 */
export const FINDING_DISPOSITION_LABEL: Record<string, string> = {
  open: 'Accept',
  rejected: 'Reject',
}

// ---------------------------------------------------------------------------
// Policy evaluation
// ---------------------------------------------------------------------------

/**
 * `PolicyOutcome` — what a policy did to one finding.
 *
 * Mirrors `PolicyOutcome.LABELS` in `app/models/uic.py`. Phrased as what the
 * policy *did*, because "which policy decided this, and what did it decide" is
 * the only question the POLICIES EVALUATED block exists to answer. Reading
 * "→ left the rule row's severity unchanged" tells a reviewer something;
 * reading "→ element_severity_stands" tells them the schema.
 */
export const POLICY_OUTCOME_LABEL: Record<string, string> = {
  evaluated: 'evaluated this run',
  fail: 'the verdict fell short of this threshold',
  raised_to_floor: "raised the severity to this policy's floor",
  element_severity_stands: "left the rule row's severity unchanged",
  gate_trigger: 'holds the case at the gate',
  reported: 'reported, does not hold the case',
}

// ---------------------------------------------------------------------------
// Gates and applications
// ---------------------------------------------------------------------------

/** `GateDecision` — past tense, because a decision is only ever read after it. */
export const GATE_DECISION_LABEL: Record<string, string> = {
  approve: 'Approved',
  reject: 'Rejected',
  request_revision: 'Revision requested',
}

/**
 * `ApplicationState`.
 *
 * This map existed twice, byte for byte, in `app/cases/[id]/page.tsx` and
 * `app/queue/page.tsx` — the queue and the case file are the two screens that
 * must agree about what a case's state is called, and they agreed by
 * coincidence.
 *
 * ⚠ **This map is also the queue's list of states.** `app/queue/page.tsx`
 * derives its filter tabs from these keys: gate-pending states make the
 * reviewer's action queue, four named states make "with the applicant" and
 * "completed", and *everything else here* makes "in progress". So a state added
 * to `ApplicationState` and not added here is worse off than a missing label —
 * `label()` would still print it readably, but a case standing in it would be
 * reachable from no tab except All. Seventeen keys, seventeen values in
 * `ApplicationState.ALL`; keep it that way.
 *
 * The queue's fifth tab, "with the public", is the one that is *not* derived
 * from this map, and it could not be: a case that has been published sits at
 * `gate_5_pending` with Gate 5 already signed, so what separates it from a case
 * still awaiting the Gate 5 decision is the gate row, not the state. See the
 * partition comment in `app/queue/page.tsx`.
 */
export const STATE_LABEL: Record<string, string> = {
  received: 'Received',
  intake: 'Intake',
  admin_completeness_review: 'Completeness review',
  gate_1_pending: 'Gate 1 · pending',
  awaiting_applicant_response: 'Awaiting applicant',
  admin_compliance: 'Admin compliance',
  gate_2_pending: 'Gate 2 · pending',
  aor_risk_screen: 'Area of Review · risk screen',
  gate_3_pending: 'Gate 3 · pending',
  technical_review: 'Technical review',
  gate_4_pending: 'Gate 4 · pending',
  draft_permit: 'Draft permit',
  gate_5_pending: 'Gate 5 · pending',
  // No `public_comment` and no `response_to_comments`. Publication happens on
  // WVDEP's own channels, so the wait between Gates 5 and 6 is not a state the
  // case stands in — it is Gate 5 signed with the publication record still open.
  gate_6_pending: 'Gate 6 · pending',
  // The two ends of Gate 6, and the only two consequential outcomes the
  // programme has. Past tense, because both are read after the fact.
  issued: 'Issued',
  denied: 'Denied',
  withdrawn: 'Withdrawn',
}

/**
 * The six Human-in-Command gates, by what a reviewer decides at each.
 *
 * Not `Gate 1` through `Gate 6`, which the number already says. RFP §4.3.2.5.4
 * gives each gate a trigger and a required action, and the action is what a
 * person opening the screen needs: the number tells them where they are, and
 * this tells them what they are being asked.
 */
export const GATE_LABEL: Record<string, string> = {
  '1': 'Administrative completeness',
  '2': 'Administrative compliance',
  '3': 'Area of Review and risk',
  '4': 'Technical review',
  '5': 'Draft permit',
  '6': 'Final decision',
}

/**
 * Which role may decide at a gate (RFP §4.3.2.5.4).
 *
 * Mirrors `ROLE_LABELS` in `app/services/gate_roles.py`. Kept in step with it,
 * for the reason `POLICY_OUTCOME_LABEL` is kept in step with its own source:
 * a display label is copy, and copy does not vary per record.
 */
export const GATE_ROLE_LABEL: Record<string, string> = {
  reviewer: 'Reviewer',
  supervisor: 'Supervisor',
  senior_reviewer: 'Senior technical reviewer',
  administrator: 'Administrator',
}

// ---------------------------------------------------------------------------
// The lifecycle, as a reviewer walks it
// ---------------------------------------------------------------------------

/**
 * The six in-system stages, by name.
 *
 * Keyed by `StageId`, not by `ApplicationState`: seventeen states map onto six
 * stages, and `lib/case-stages.ts` owns that mapping. These are the words on the
 * rail.
 *
 * There is no public-notice stage and no response-to-comments stage. Publication
 * happens on WVDEP's own channels; the rail draws it as a break in the track
 * between Gates 5 and 6 rather than as a place a case sits.
 */
export const STAGE_LABEL: Record<string, string> = {
  intake: 'Intake and classification',
  completeness: 'Administrative completeness',
  compliance: 'Administrative compliance',
  aor: 'Area of Review and risk',
  technical: 'Technical review',
  permit: 'Permit and decision',
}

/**
 * The one-line question each stage answers, for the stage pane's header.
 *
 * **Quoted from `app/services/gate_stages.py`, not paraphrased.** That module
 * holds the agency's own wording for its own process, and the same strings go
 * into the gate package a reviewer signs. A screen that asked the question in
 * different words from the record of the decision would be two accounts of one
 * act.
 *
 * Two do not come from there, for reasons worth stating:
 *
 * * **`intake`** is ungated, so it has no `GateStage` row to quote. The wording
 *   follows the same voice.
 * * **`permit`** quotes *Gate 5's* question rather than Gate 6's. Gate 6's asks
 *   whether "the public comment period closed with a response to comments
 *   prepared" — and the response to comments is out of scope, so quoting it here
 *   would put a promise on screen the build does not keep. `gate_stages.py`
 *   needs the same correction.
 */
export const STAGE_QUESTION: Record<string, string> = {
  intake:
    'Which class is this application, what tracking number does it carry, and is the case file open?',
  completeness:
    'Is every regulatory element the programme requires present in this package, and does the document supplied actually address it?',
  compliance:
    'Was the correct fee paid, does the applicant have legal standing to inject, and what will the public notice require?',
  aor: 'Is the review footprint drawn correctly, and what lies inside it that could carry fluid to drinking water?',
  technical:
    "Does the applicant's own arithmetic hold: will the injection stay below the pressure that cracks the seal, is the well built to contain it, and is the money there to close the site?",
  permit:
    'Does every drafted condition trace to an approved technical finding and the provision behind it?',
}

/**
 * Who is holding a parked case.
 *
 * The two waits are structurally identical — nothing in the Command Center can
 * advance either, both carry a deadline the system computed, and both end with a
 * human attesting that the outside world did something. One component renders
 * both; these are the two words that differ.
 */
export const PARKED_LABEL: Record<string, string> = {
  applicant: 'With the applicant',
  public: 'With the public',
}

// ---------------------------------------------------------------------------
// Geospatial
// ---------------------------------------------------------------------------

/**
 * `AorType` — how the Area of Review was arrived at.
 *
 * The two classes compute it completely differently, and the labels say so
 * rather than naming the enum: a Class I footprint is a circle the agency draws
 * itself, and a Class VI footprint is an irregular polygon the applicant
 * modelled and the agency validates.
 */
export const AOR_TYPE_LABEL: Record<string, string> = {
  class_i_fixed: 'Fixed quarter-mile radius',
  class_vi_modelled: 'Modelled by the applicant',
}

/**
 * `AorBasis` — what the footprint is drawn around.
 *
 * The distinction the whole delineation turns on. The plume is where the
 * injected fluid physically ends up; the pressure front is how far the push
 * travels through the water already down there, and it reaches very much
 * further.
 */
export const AOR_BASIS_LABEL: Record<string, string> = {
  pressure_front: 'Pressure front',
  plume: 'Plume',
  fixed_radius: 'Fixed radius',
}

/**
 * `AorProvenance` — who drew this footprint.
 *
 * "Revised by a reviewer" rather than "reviewer", because the point of the
 * value is that a person changed what the system produced, and the row it
 * supersedes is still there to compare against.
 */
export const AOR_PROVENANCE_LABEL: Record<string, string> = {
  ai: 'Delineated by the AI Employee',
  reviewer: 'Revised by a reviewer',
}

/** `LayerFormat` — how a geospatial layer arrived. */
export const LAYER_FORMAT_LABEL: Record<string, string> = {
  shapefile: 'Shapefile',
  geojson: 'GeoJSON',
  kml: 'KML',
  file_gdb: 'ESRI File Geodatabase',
}

// ---------------------------------------------------------------------------
// Fees and public comment
// ---------------------------------------------------------------------------

/** `FeeType` — which fee a schedule row sets. */
export const FEE_TYPE_LABEL: Record<string, string> = {
  application: 'Permit application',
  annual: 'Annual permit fee',
  major_mod: 'Major modification',
  minor_mod: 'Minor modification',
  resubmittal: 'Resubmittal',
  completion_certificate: 'Completion certificate',
  operation_per_ton: 'Operation fee',
}

/**
 * `FeeUnit` — what the amount is multiplied by.
 *
 * `per_wellhead` is the one that bites: the Class VI application fee is charged
 * for each injection wellhead, so a package short by one wellhead is short by
 * the whole per-wellhead amount.
 */
export const FEE_UNIT_LABEL: Record<string, string> = {
  flat: 'Flat',
  per_wellhead: 'Per wellhead',
  per_ton: 'Per ton injected',
}

// ---------------------------------------------------------------------------
// Documents and ingestion
// ---------------------------------------------------------------------------

/**
 * `ParseStatus`.
 *
 * `catalogued` is the interesting one: a GIS payload that was recorded but
 * deliberately not read until Phase 3. "Recorded, not read" says that; the
 * identifier implies it was filed away successfully, which is nearly the
 * opposite of the truth a reviewer needs.
 */
export const PARSE_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  converting: 'Converting',
  parsed: 'Read',
  quarantined: 'Quarantined',
  failed: 'Failed',
  catalogued: 'Recorded, not read',
}

/** `RunStatus` — an ingestion run. */
export const RUN_STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
}

// ---------------------------------------------------------------------------
// Drafted documents
// ---------------------------------------------------------------------------

/**
 * `ArtifactKind` — every document the AI Employee drafts.
 *
 * The first two names are `NoticeTab`'s, kept verbatim rather than
 * "improved": "Notice of Administrative Completeness" is what WVDEP calls that
 * letter in the corpus, and a shorter label would be this product inventing a
 * name for an instrument that already has one. The other four are declared in
 * the model because they are named in the corpus too, so a Phase 3 screen that
 * renders one does not have to rediscover what to call it.
 *
 * There is no `rtc`. The Response to Comments is drafted outside this system
 * along with the publication it answers, so the Command Center never holds one
 * and a label for it would be a name for a document this build cannot produce.
 */
export const ARTIFACT_KIND_LABEL: Record<string, string> = {
  nod: 'Notice of Deficiency',
  completeness_letter: 'Notice of Administrative Completeness',
  draft_permit: 'Draft permit',
  fact_sheet: 'Fact sheet',
  export_package: 'Export package',
  technical_review_package: 'Technical review package',
}

/**
 * `ArtifactStatus`, again `NoticeTab`'s wording.
 *
 * `held` is not a synonym for `draft` and the label carries that: the same
 * bytes, but a reviewer has opened them, which is what makes "nobody has looked
 * at this yet" an answerable question. `transmitted` is a record of a human act,
 * never something the system did. `superseded` reads "Discarded" because that is
 * what the reviewer did to get there.
 */
export const ARTIFACT_STATUS_LABEL: Record<string, string> = {
  draft: 'Drafted, not yet opened',
  held: 'Held for your approval',
  approved: 'Approved',
  transmitted: 'Transmitted',
  superseded: 'Discarded',
}

/** `TransmissionChannel` — how a human sent it. Recorded, never performed. */
export const TRANSMISSION_CHANNEL_LABEL: Record<string, string> = {
  email: 'Email',
  mail: 'Mail',
  portal: 'Portal',
  other: 'Other',
}

// ---------------------------------------------------------------------------
// Insights
// ---------------------------------------------------------------------------

/**
 * `InsightSeverity` (`app/schemas/ai.py`).
 *
 * Six values, where the UIC side has four. Worth labelling even though each is
 * already a single readable word, so the insights screen and the case file stop
 * being the only two places in the app that disagree about whether an enum gets
 * a map.
 */
export const INSIGHT_SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  warning: 'Warning',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
}
